import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getIdMap, safeExtractFromObject } from '../../helpers/helpers';

/** Keys stored on OT profiles and read from merged entity settings (company + profile override). */
const OT_PROFILE_SETTING_KEYS = [
  'enableManualOT',
  'manualOTModifier',
  'weeklyOvertime',
  'dailyOvertime',
  'dailyDouble',
  'saturdayPay',
  'saturdayOvertime',
  'sundayPay',
  'sundayOvertime',
  'otEligibilitySelectionEnabled',
  'workweekStartDay',
  'enableNightShiftPremium',
];

/**
 * For each OT-related key, uses `profileSettings[key]` when the profile defines that key;
 * otherwise falls back to `companySettings`.
 */
export function mergeEntityOTSettings(settings, companySettings) {
  const merged = {
    ...companySettings,
  };
  OT_PROFILE_SETTING_KEYS.forEach((key) => {
    delete merged[key];
    merged[key] = settings?.[key];
  });
  return merged;
}

export default function useEntityOTProfileMap({
  userIds,
  divisionIds,
}) {
  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);
  const otProfiles = useSelector((state) => state.settings.otProfiles);
  const companySettings = useSelector((state) => safeExtractFromObject(state.settings.company, 'settings')) || {};

  const otProfileMap = useMemo(() => (
    otProfiles.reduce((acc, profile) => {
      acc[profile.id] = profile;
      return acc;
    }, {})
  ), [otProfiles]);

  const userMap = useMemo(() => getIdMap(users), [users]);
  return useMemo(() => {
    const map = {};
    const usersWithNoProfile = [];
    const usersWithProfile = new Set();
    userIds?.forEach((id) => {
      const relevantProfile = otProfileMap[userMap[id]?.otProfileId];
      if (!relevantProfile?.settings && !relevantProfile?.isCompanyProfile) {
        usersWithNoProfile.push(id);
      }
      map[id] = mergeEntityOTSettings(
        relevantProfile?.settings ?? companySettings,
        companySettings,
      );
    });

    divisionIds?.forEach((id) => {
      const relevantProfile = otProfileMap[divisions[id]?.otProfileId];
      map[id] = mergeEntityOTSettings(
        relevantProfile?.settings ?? companySettings,
        companySettings,
      );

      // If the user has no profile, we will look for a division profile
      // Otherwise it uses the company profile
      usersWithNoProfile.forEach((userId) => {
        if (
          (relevantProfile?.settings || relevantProfile?.isCompanyProfile)
          && divisions[id]?.users?.has(userId)
        ) {
          map[userId] = map[id];
          usersWithProfile.add(userId);
        }
      });
    });

    usersWithNoProfile.forEach((userId) => {
      if (usersWithProfile.has(userId)) return;
      map[userId] = companySettings;
    });
    return map;
  }, [userIds, divisionIds, companySettings, otProfileMap, userMap, divisions]);
}
